import { Box, Grid, TextField, IconButton, Tooltip, Stack, Button, Typography } from "@mui/material";
import { useFieldArray, useForm } from "react-hook-form";
import { FormProvider, RHFMultipleSelectField, RHFSelectField, RHFSwitch } from "../../common/hook-form";
import { MerchantFilterCached } from "../../common/ProshipFormComponents";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ElectricalServicesOutlinedIcon from "@mui/icons-material/ElectricalServicesOutlined";
import { apiConstants, get, post } from "../../common/constants";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { LoadingButton } from "@mui/lab";
import PageTitle from "../../components/PageTitle";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from 'yup';
import Loader from "../../common/Loader";
import {
  T, cardSx, primaryBtnSx, ghostBtnSx, fieldSx, FieldLabel,
  SectionCard, Pill, EmptyState, activeTone, ORDER_STATUS_OPTIONS, LOCKED_WEBHOOK_URL,
  FORMAT_TYPE_OPTIONS,
} from "./webhookUi";

const BLANK_HEADER = { key: "", value: "" };
const BLANK = { webHookUrl: "", subscribedEvents: "", isWebHookActive: false, headers: [{ ...BLANK_HEADER }], formatType: "PROSHIP" };

/* Custom headers for a single webhook. react-hook-form needs a nested field array
   to be scoped to its own component, one instance per parent (webhook) row. */
function CustomHeaderFields({ nestIndex, control, register, locked }) {
  const { fields, append, remove } = useFieldArray({ control, name: `list.${nestIndex}.headers` });

  return (
    <>
      <Stack spacing={1.25}>
        {fields.map((field, i) => (
          <Grid container spacing={1.5} key={field.id} alignItems="center">
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth size="small" variant="outlined" placeholder="Header key — e.g. Authorization"
                disabled={locked} sx={fieldSx}
                {...register(`list.${nestIndex}.headers.${i}.key`)}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth size="small" variant="outlined" placeholder="Header value — e.g. Bearer <token>"
                disabled={locked} sx={fieldSx}
                {...register(`list.${nestIndex}.headers.${i}.value`)}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              {!locked && fields.length > 1 && (
                <Tooltip title="Remove this header">
                  <IconButton size="small" onClick={() => remove(i)} sx={{ color: T.RED, '&:hover': { bgcolor: T.RED_BG } }}>
                    <DeleteOutlineOutlinedIcon sx={{ fontSize: 19 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Grid>
          </Grid>
        ))}
      </Stack>

      {!locked && (
        <Button
          size="small"
          startIcon={<AddCircleOutlineOutlinedIcon sx={{ fontSize: 18 }} />}
          onClick={() => append({ ...BLANK_HEADER })}
          sx={{ ...ghostBtnSx, mt: 1.25, py: 0.6 }}
        >
          Add another header
        </Button>
      )}
    </>
  );
}

function ClickPostWebhook() {
  const { enqueueSnackbar } = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        list: Yup.array().of(
          Yup.object().shape({
            webHookUrl: Yup.string().required("Webhook URL is required").url("Please enter a valid URL"),
          })
        ),
      })
    ),
    defaultValues: { merchant: "", list: [BLANK] },
  });
  const { handleSubmit, control, register, setValue, reset, watch, formState: { errors } } = methods;
  const merchantId = watch('merchant');
  const listValues = watch('list') || [];
  const { fields, append, remove } = useFieldArray({ control, name: "list" });

  // Locked strictly per row: only the ProShip system webhook itself is uneditable.
  // (This used to OR in a form-wide flag set from row 0, which locked every other
  // webhook whenever the merchant's first one happened to be the system hook.)
  const isLocked = (idx) => listValues?.[idx]?.webHookUrl === LOCKED_WEBHOOK_URL;

  const onSubmit = async (data) => {
    const updateList = data.list.map((item) => {
      const obj = {
        webHookUrl: item.webHookUrl,
        isWebHookActive: !!item.isWebHookActive,
        // BUG FIX: carry each webhook's formatType through the save. Previously it
        // was omitted, so the backend re-defaulted the format for every hook.
        formatType: item.formatType || "PROSHIP",
      };
      obj.subscribedEvents = item?.subscribedEvents?.length > 0
        ? item.subscribedEvents.map((d) => (d === "ALL" ? null : d))
        : [];
      // Optional custom auth headers attached to every webhook call. Backend expects
      // additionalData as a LIST of entries; `name` is a fixed discriminator. Rows the
      // user left blank are dropped rather than sent as empty headers.
      const headers = (item.headers || [])
        .filter((h) => h?.key?.trim())
        .map((h) => ({ name: "Header", key: h.key.trim(), value: h.value ?? "" }));
      if (headers.length > 0) {
        obj.additionalData = headers;
      }
      return obj;
    });
    setIsSubmitting(true);
    const request = { merchantId, webHooks: updateList };
    await post(apiConstants.WEBHOOK, request).then((res) => {
      if (res.data.responseObj === "success") {
        enqueueSnackbar("Webhook saved successfully", { variant: 'success' });
      } else {
        enqueueSnackbar(res.data.responseObj, { variant: 'error' });
      }
    }).catch((err) => {
      console.log(err);
      enqueueSnackbar(err?.response?.data?.message || "Could not save webhook", { variant: 'error' });
    });
    setIsSubmitting(false);
  };

  useEffect(() => {
    if (!merchantId) return;
    setIsLoading(true);
    get(`${apiConstants.WEBHOOK_SEARCH}?merchantId=${merchantId}`)
      .then((res) => {
        // Show every webhook configured for the merchant, whatever its formatType.
        // Previously this tab rendered only PROSHIP/legacy-null hooks and hid the rest,
        // which left INCREFF (and PROSHIP_WITH_SHIPMENT_HISTORY) invisible and uneditable.
        const mine = res?.data || [];
        reset({ merchant: merchantId, list: [BLANK] });
        if (mine.length === 0) return;
        mine.forEach((item, index) => {
          // Every 'Header' entry is shown; previously only the first was read back,
          // so a webhook with multiple headers silently lost all but one on save.
          const headerEntries = (item.additionalData || []).filter((d) => d?.name === 'Header');
          const row = {
            webHookUrl: item.webHookUrl,
            subscribedEvents: item.subscribedEvents || [],
            isWebHookActive: !!item.isWebHookActive,
            headers: headerEntries.length > 0
              ? headerEntries.map((d) => ({ key: d.key || '', value: d.value ?? '' }))
              : [{ ...BLANK_HEADER }],
            formatType: item.formatType || "PROSHIP",
          };
          if (index === 0) {
            setValue('list.0.webHookUrl', row.webHookUrl);
            setValue('list.0.subscribedEvents', row.subscribedEvents);
            setValue('list.0.isWebHookActive', row.isWebHookActive);
            setValue('list.0.headers', row.headers);
            setValue('list.0.formatType', row.formatType);
          } else {
            append(row);
          }
        });
      })
      .catch((error) => console.error('Error fetching data:', error))
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId]);

  const addBlankField = () => append({ ...BLANK });

  return (
    <>
      {isLoading && <Loader />}
      <PageTitle>Merchant Webhooks</PageTitle>
      <Box>
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          {/* Merchant picker */}
          <SectionCard
            title="Order Status Update Webhooks"
            subtitle="Push real-time order lifecycle events to a merchant's endpoint."
            icon={<Box sx={{ width: 38, height: 38, borderRadius: '10px', bgcolor: T.BLUE_BG, display: 'grid', placeItems: 'center' }}><ElectricalServicesOutlinedIcon sx={{ color: T.BLUE, fontSize: 22 }} /></Box>}
            sx={{ mb: 2 }}
          >
            <FieldLabel required>Merchant</FieldLabel>
            <Box sx={{ maxWidth: 460, ...fieldSx }}>
              <MerchantFilterCached fullWidth placeholder="Select a merchant…" />
            </Box>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1 }}>
              <StorefrontOutlinedIcon sx={{ fontSize: 15, color: T.MUTED }} />
              <Typography sx={{ fontSize: 12, color: T.MUTED }}>Pick a merchant to view and manage its webhooks.</Typography>
            </Stack>
          </SectionCard>

          {!merchantId ? (
            <EmptyState
              icon={<Box sx={{ width: 56, height: 56, borderRadius: '16px', bgcolor: T.BLUE_BG, display: 'grid', placeItems: 'center', mx: 'auto' }}><HubOutlinedIcon sx={{ fontSize: 30, color: T.BLUE }} /></Box>}
              title="No merchant selected"
              subtitle="Choose a merchant above to configure its order-status webhooks."
            />
          ) : (
            <>
              <Stack spacing={2}>
                {fields.map((field, index) => {
                  const locked = isLocked(index);
                  const active = !!listValues?.[index]?.isWebHookActive;
                  const eventCount = (listValues?.[index]?.subscribedEvents || []).filter(Boolean).length;
                  return (
                    <SectionCard
                      key={field.id}
                      headerBg={active ? T.GREEN_BG : T.LIGHT}
                      icon={<Box sx={{ width: 32, height: 32, borderRadius: '9px', bgcolor: '#fff', border: `1px solid ${T.BORDER}`, display: 'grid', placeItems: 'center', fontWeight: 800, fontSize: 13, color: T.NAVY }}>{index + 1}</Box>}
                      title={`Webhook ${index + 1}`}
                      subtitle={eventCount > 0 ? `${eventCount} event${eventCount === 1 ? '' : 's'} subscribed` : 'All events'}
                      action={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Pill tone={activeTone(active)} dot>{active ? 'Active' : 'Inactive'}</Pill>
                          {locked ? (
                            <Tooltip title="System webhook — managed by ProShip, cannot be edited">
                              <Box><Pill tone={{ bg: '#EEF1F5', fg: T.MUTED }}><LockOutlinedIcon sx={{ fontSize: 12, mr: 0.3 }} />Locked</Pill></Box>
                            </Tooltip>
                          ) : (
                            fields.length > 1 && (
                              <Tooltip title="Remove this webhook">
                                <IconButton size="small" onClick={() => remove(index)} sx={{ color: T.RED, '&:hover': { bgcolor: T.RED_BG } }}>
                                  <DeleteOutlineOutlinedIcon sx={{ fontSize: 19 }} />
                                </IconButton>
                              </Tooltip>
                            )
                          )}
                        </Stack>
                      }
                    >
                      <Grid container spacing={2.5}>
                        <Grid item xs={12} md={8}>
                          <FieldLabel required>Webhook URL</FieldLabel>
                          <TextField
                            fullWidth size="small" variant="outlined" placeholder="https://your-endpoint.com/webhook"
                            disabled={locked}
                            sx={fieldSx}
                            {...register(`list.${index}.webHookUrl`)}
                            error={Boolean(errors?.list?.[index]?.webHookUrl?.message)}
                            helperText={errors?.list?.[index]?.webHookUrl?.message}
                          />
                        </Grid>
                        <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                          <Box sx={{ px: 1.5, py: 0.75, borderRadius: '10px', border: `1px solid ${T.BORDER}`, bgcolor: active ? T.GREEN_BG : '#F7F9FC', width: '100%' }}>
                            <RHFSwitch name={`list.${index}.isWebHookActive`} label={active ? 'Webhook active' : 'Webhook inactive'} color="success" disabled={locked} />
                          </Box>
                        </Grid>

                        <Grid item xs={12} md={5}>
                          <FieldLabel required>Format Type</FieldLabel>
                          <Box sx={fieldSx}>
                            <RHFSelectField
                              width="100%"
                              name={`list.${index}.formatType`}
                              disabled={locked}
                              options={FORMAT_TYPE_OPTIONS}
                              label=""
                              placeholder="Select format type"
                            />
                          </Box>
                          <Typography sx={{ fontSize: 11.5, color: T.MUTED, mt: 0.75 }}>Payload format pushed to this endpoint. Defaults to PROSHIP.</Typography>
                        </Grid>

                        <Grid item xs={12}>
                          <FieldLabel>Subscribed Events</FieldLabel>
                          <Box sx={fieldSx}>
                            <RHFMultipleSelectField
                              width="100%" search
                              name={`list.${index}.subscribedEvents`}
                              disabled={locked}
                              options={ORDER_STATUS_OPTIONS}
                              label=""
                              placeholder="Leave empty to receive all events"
                            />
                          </Box>
                          <Typography sx={{ fontSize: 11.5, color: T.MUTED, mt: 0.75 }}>Select specific lifecycle events, or leave empty to receive every event.</Typography>
                        </Grid>

                        <Grid item xs={12}>
                          <FieldLabel>Custom Header <Box component="span" sx={{ textTransform: 'none', fontWeight: 500, color: T.MUTED }}>(optional)</Box></FieldLabel>
                          <CustomHeaderFields nestIndex={index} control={control} register={register} locked={locked} />
                          <Typography sx={{ fontSize: 11.5, color: T.MUTED, mt: 0.75 }}>Sent with every webhook call so your endpoint can verify the request.</Typography>
                        </Grid>
                      </Grid>
                    </SectionCard>
                  );
                })}
              </Stack>

              <Button fullWidth startIcon={<AddCircleOutlineOutlinedIcon />} onClick={addBlankField} sx={{ ...ghostBtnSx, mt: 2 }}>
                Add another webhook
              </Button>

              {/* Sticky save bar */}
              <Box sx={{ ...cardSx, position: 'sticky', bottom: 12, mt: 2, px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 -2px 12px rgba(16,24,40,0.08)' }}>
                <Typography sx={{ fontSize: 12.5, color: T.MUTED }}>
                  {fields.length} webhook{fields.length === 1 ? '' : 's'} for this merchant
                </Typography>
                <LoadingButton loading={isSubmitting} variant="contained" type="submit" sx={primaryBtnSx}>
                  Save changes
                </LoadingButton>
              </Box>
            </>
          )}
        </FormProvider>
      </Box>
    </>
  );
}

export default ClickPostWebhook;
